import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Loader2 } from 'lucide-react';
import { zoekVakgebieden, type Vakgebied } from '../../services/proAcademyApi';

/**
 * A. Vakgebied- & Specialismenzoeker: zoekbare dropdown over alle specialismen.
 * Typfouten ("arbo arts", "traumatolog", "verzuim crov") landen via de trigram-zoeker
 * van de server. Enter zonder keuze = vrije tekstzoekopdracht op de catalogus.
 */
export const VakgebiedZoeker: React.FC<{
  vak: Vakgebied | Omit<Vakgebied, 'score'> | null;
  vrijeTekst: string | null;
  onKiesVak: (v: Vakgebied) => void;
  onVrijeTekst: (q: string | null) => void;
  onWis: () => void;
}> = ({ vak, vrijeTekst, onKiesVak, onVrijeTekst, onWis }) => {
  const { t } = useTranslation('pages');
  const [tekst, setTekst] = useState(vrijeTekst ?? '');
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(0);
  const [debounced, setDebounced] = useState('');
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => setTekst(vrijeTekst ?? ''), [vrijeTekst]);
  useEffect(() => {
    const h = window.setTimeout(() => setDebounced(tekst.trim()), 220);
    return () => window.clearTimeout(h);
  }, [tekst]);
  useEffect(() => {
    const sluit = (e: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', sluit);
    return () => document.removeEventListener('mousedown', sluit);
  }, []);

  const { data, isFetching } = useQuery({
    queryKey: ['pro-academy-vakgebieden', debounced],
    queryFn: () => zoekVakgebieden(debounced),
    enabled: open && !vak,
    staleTime: 5 * 60_000,
  });
  const hits = useMemo(() => data ?? [], [data]);
  const heeftTekst = tekst.trim().length >= 2;
  /** Eerst de specialismen (beste match bovenaan = standaardkeuze), als laatste de vrije tekstzoekopdracht. */
  const opties = hits.length + (heeftTekst ? 1 : 0);
  const vrijeTekstIdx = hits.length;

  useEffect(() => setActief(0), [hits, heeftTekst]);

  const kies = (i: number) => {
    if (heeftTekst && i === vrijeTekstIdx) {
      onVrijeTekst(tekst.trim());
    } else {
      const hit = hits[i];
      if (hit) onKiesVak(hit);
    }
    setTekst('');
    setOpen(false);
  };

  const toets = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActief((a) => Math.min(opties - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActief((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (opties > 0) kies(actief); }
    else if (e.key === 'Escape') { setOpen(false); }
    else if (e.key === 'Backspace' && !tekst && (vak || vrijeTekst)) { onWis(); }
  };

  return (
    <div ref={wrapper} className="relative w-full min-w-0">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white py-2 pl-3 pr-2 focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-500/30">
        <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
        {vak && (
          <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#0B1F3A] px-2.5 py-0.5 text-xs font-semibold text-white">
            <span className="truncate">{vak.label}</span>
            <button type="button" onClick={onWis} aria-label={t('proAcademy.wisVakgebied', { defaultValue: 'Vakgebied wissen' })} className="rounded-full hover:bg-white/20">
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        )}
        {!vak && vrijeTekst && (
          <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-900">
            <span className="truncate">“{vrijeTekst}”</span>
            <button type="button" onClick={onWis} aria-label={t('proAcademy.wisZoekterm', { defaultValue: 'Zoekterm wissen' })} className="rounded-full hover:bg-gray-200">
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        )}
        <input
          ref={input}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="vakgebied-opties"
          aria-autocomplete="list"
          value={tekst}
          onChange={(e) => { setTekst(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={toets}
          placeholder={vak ? t('proAcademy.zoekBinnen', { defaultValue: 'Zoek binnen dit vakgebied…' }) : t('proAcademy.zoekPlaceholder', { defaultValue: 'Selecteer uw vakgebied of rol (bijv. Bedrijfsarts, Burn-out Coach)…' })}
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
        />
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
      </div>

      {open && (opties > 0 || (heeftTekst && !isFetching)) && !vak && (
        <ul id="vakgebied-opties" role="listbox" className="absolute left-0 right-0 top-full z-40 mt-1 max-h-80 overflow-y-auto rounded-2xl border border-gray-200 bg-white py-1 shadow-xl">
          {hits.map((h, idx) => {
            return (
              <li
                key={h.professional_type}
                role="option"
                aria-selected={actief === idx}
                onMouseDown={(e) => { e.preventDefault(); kies(idx); }}
                onMouseEnter={() => setActief(idx)}
                className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm ${actief === idx ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <span className="truncate">{h.label}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                  {t('proAcademy.rijKort', { defaultValue: 'Rij {{n}}', n: h.rij })} · {h.hoofdmap_label}
                </span>
              </li>
            );
          })}
          {heeftTekst && (
            <li
              role="option"
              aria-selected={actief === vrijeTekstIdx}
              onMouseDown={(e) => { e.preventDefault(); kies(vrijeTekstIdx); }}
              onMouseEnter={() => setActief(vrijeTekstIdx)}
              className={`cursor-pointer border-t border-gray-200 px-3 py-2 text-sm ${actief === vrijeTekstIdx ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <Search className="mr-2 inline h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
              {t('proAcademy.zoekOpTekst', { defaultValue: 'Zoek in de catalogus op “{{q}}”', q: tekst.trim() })}
              {hits.length === 0 && !isFetching && (
                <span className="ml-2 text-xs text-gray-500">{t('proAcademy.geenVakgebied', { defaultValue: '(geen specialisme gevonden)' })}</span>
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default VakgebiedZoeker;
